import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, Play, Pause, RotateCw, Volume2, VolumeX, Minus, Plus } from 'lucide-react';

const COLS = 10;
const ROWS = 20;
const BASE_TICK_MS = 500;
const BASE_BPM = 180;

const SHAPES = [
  { shape: [[1,1,1,1]], color: '#00f0f0' },
  { shape: [[1,1],[1,1]], color: '#f0f000' },
  { shape: [[0,1,0],[1,1,1]], color: '#a000f0' },
  { shape: [[1,0,0],[1,1,1]], color: '#0000f0' },
  { shape: [[0,0,1],[1,1,1]], color: '#f0a000' },
  { shape: [[0,1,1],[1,1,0]], color: '#00f000' },
  { shape: [[1,1,0],[0,1,1]], color: '#f00000' },
];

const labels = {
  fr: { title: 'Un petit truc en plus', score: 'Score', lines: 'Lignes', level: 'Niveau', start: 'Jouer', pause: 'Pause', resume: 'Reprendre', gameOver: 'GAME OVER', restart: 'Rejouer', back: 'Retour', next: 'Suivant', controls: 'Contrôles', left: 'Gauche', right: 'Droite', down: 'Bas', rotate: 'Tourner', drop: 'Drop' },
  ru: { title: 'Маленький бонус', score: 'Счёт', lines: 'Линии', level: 'Уровень', start: 'Играть', pause: 'Пауза', resume: 'Продолжить', gameOver: 'ИГРА ОКОНЧЕНА', restart: 'Заново', back: 'Назад', next: 'Следующий', controls: 'Управление', left: 'Влево', right: 'Вправо', down: 'Вниз', rotate: 'Вращать', drop: 'Бросить' },
  ko: { title: '작은 보너스', score: '점수', lines: '라인', level: '레벨', start: '시작', pause: '일시정지', resume: '재개', gameOver: '게임 오버', restart: '재시작', back: '뒤로', next: '다음', controls: '조작법', left: '왼쪽', right: '오른쪽', down: '아래', rotate: '회전', drop: '드롭' },
  zh: { title: '小彩蛋', score: '分数', lines: '行数', level: '等级', start: '开始', pause: '暂停', resume: '继续', gameOver: '游戏结束', restart: '重新开始', back: '返回', next: '下一个', controls: '操控', left: '左', right: '右', down: '下', rotate: '旋转', drop: '坠落' },
};

// Korobeiniki (Russian folk / Tetris theme) - note frequencies & durations
const NOTE_FREQS = { E4:329.63, B3:246.94, C4:261.63, D4:293.66, F4:349.23, A3:220, G3:196, E3:164.81, A4:440, G4:392, F3:174.61, D3:146.83 };
const MELODY = [
  // Part A
  ['E4',4],['B3',2],['C4',2],['D4',4],['C4',2],['B3',2],
  ['A3',4],['A3',2],['C4',2],['E4',4],['D4',2],['C4',2],
  ['B3',4],['B3',2],['C4',2],['D4',4],['E4',4],
  ['C4',4],['A3',4],['A3',4],[null,4],
  // Part A repeat with variation
  ['D4',6],['F4',2],['A4',4],['G4',2],['F4',2],
  ['E4',6],['C4',2],['E4',4],['D4',2],['C4',2],
  ['B3',4],['B3',2],['C4',2],['D4',4],['E4',4],
  ['C4',4],['A3',4],['A3',4],[null,4],
  // Part B
  ['E4',8],['C4',8],['D4',8],['B3',8],
  ['C4',8],['A3',8],['G3',8],['B3',8],
  ['E4',8],['C4',8],['D4',8],['B3',8],
  ['C4',4],['E4',4],['A4',8],['G4',8],[null,4],
];

const BASS = [
  ['E3',4],['E3',4],['E3',4],['E3',4],
  ['A3',4],['A3',4],['A3',4],['A3',4],
  ['G3',4],['G3',4],['G3',4],['G3',4],
  ['A3',4],['A3',4],['A3',4],['A3',4],
  ['F3',4],['F3',4],['F3',4],['F3',4],
  ['E3',4],['E3',4],['E3',4],['E3',4],
  ['G3',4],['G3',4],['G3',4],['G3',4],
  ['A3',4],['A3',4],['A3',4],['A3',4],
  ['E3',8],['E3',8],['D3',8],['D3',8],
  ['A3',8],['A3',8],['G3',8],['G3',8],
  ['E3',8],['E3',8],['D3',8],['D3',8],
  ['A3',4],['A3',4],['A3',8],['G3',8],[null,4],
];

class KorobeinikiPlayer {
  constructor(bpm = 180) {
    this.ctx = null;
    this.playing = false;
    this.timeouts = [];
    this.bpm = bpm;
  }
  setBpm(bpm) {
    this.bpm = bpm;
  }
  start() {
    if (this.playing) return;
    this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    this.playing = true;
    this._loop();
  }
  _loop() {
    if (!this.playing || !this.ctx) return;
    const beatSec = 60 / this.bpm / 2;
    let t = this.ctx.currentTime + 0.05;

    // Melody voice (square wave)
    let mt = t;
    for (const [note, dur] of MELODY) {
      const d = dur * beatSec;
      if (note && NOTE_FREQS[note]) {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = 'square';
        osc.frequency.value = NOTE_FREQS[note];
        gain.gain.setValueAtTime(0.08, mt);
        gain.gain.exponentialRampToValueAtTime(0.001, mt + d - 0.02);
        osc.connect(gain);
        gain.connect(this.ctx.destination);
        osc.start(mt);
        osc.stop(mt + d);
      }
      mt += d;
    }

    // Bass voice (triangle wave)
    let bt = t;
    for (const [note, dur] of BASS) {
      const d = dur * beatSec;
      if (note && NOTE_FREQS[note]) {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.value = NOTE_FREQS[note] * 0.5;
        gain.gain.setValueAtTime(0.06, bt);
        gain.gain.exponentialRampToValueAtTime(0.001, bt + d - 0.02);
        osc.connect(gain);
        gain.connect(this.ctx.destination);
        osc.start(bt);
        osc.stop(bt + d);
      }
      bt += d;
    }

    const loopDuration = mt - t;
    const tid = setTimeout(() => this._loop(), loopDuration * 1000 - 100);
    this.timeouts.push(tid);
  }
  stop() {
    this.playing = false;
    this.timeouts.forEach(clearTimeout);
    this.timeouts = [];
    if (this.ctx) { this.ctx.close(); this.ctx = null; }
  }
}

// Game Over melody - sad descending 8-bit tune
function playGameOverMelody() {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const notes = [
    [392, 0.3], [349.23, 0.3], [329.63, 0.3], [293.66, 0.3],
    [261.63, 0.4], [246.94, 0.4], [220, 0.6],
    [null, 0.2],
    [164.81, 0.8],
  ];
  let t = ctx.currentTime + 0.05;
  for (const [freq, dur] of notes) {
    if (freq) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.1, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + dur - 0.02);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + dur);
    }
    t += dur;
  }
  setTimeout(() => ctx.close(), (t - ctx.currentTime) * 1000 + 200);
}

const createEmptyBoard = () => Array.from({ length: ROWS }, () => Array(COLS).fill(null));
const randomPiece = () => {
  const p = SHAPES[Math.floor(Math.random() * SHAPES.length)];
  return { shape: p.shape.map(r => [...r]), color: p.color, row: 0, col: Math.floor((COLS - p.shape[0].length) / 2) };
};
const rotate = (matrix) => matrix[0].map((_, i) => matrix.map(r => r[i]).reverse());
const isValid = (board, piece, dr = 0, dc = 0, newShape = null) => {
  const s = newShape || piece.shape;
  for (let r = 0; r < s.length; r++)
    for (let c = 0; c < s[r].length; c++)
      if (s[r][c]) {
        const nr = piece.row + r + dr, nc = piece.col + c + dc;
        if (nr < 0 || nr >= ROWS || nc < 0 || nc >= COLS) return false;
        if (board[nr][nc]) return false;
      }
  return true;
};
const merge = (board, piece) => {
  const b = board.map(r => [...r]);
  piece.shape.forEach((row, r) => row.forEach((v, c) => { if (v) b[piece.row + r][piece.col + c] = piece.color; }));
  return b;
};
const clearLines = (board) => {
  const kept = board.filter(r => r.some(c => !c));
  const cleared = ROWS - kept.length;
  const empty = Array.from({ length: cleared }, () => Array(COLS).fill(null));
  return { board: [...empty, ...kept], cleared };
};

const CELL = 28;
const MOSCOW_BG = 'https://images.unsplash.com/photo-1642013002976-e916cd692289?w=1920&h=1080&fit=crop&q=80';

const Tetris = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const l = labels[language] || labels.fr;

  const [board, setBoard] = useState(createEmptyBoard);
  const [piece, setPiece] = useState(null);
  const [nextPiece, setNextPiece] = useState(null);
  const [score, setScore] = useState(0);
  const [lines, setLines] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [running, setRunning] = useState(false);
  const [musicOn, setMusicOn] = useState(true);
  const [bpm, setBpm] = useState(300);
  const tickRef = useRef(null);
  const stateRef = useRef({ board, piece, score, lines });
  const musicRef = useRef(null);

  useEffect(() => { stateRef.current = { board, piece, score, lines }; }, [board, piece, score, lines]);

  const level = Math.floor(lines / 10) + 1;
  const bpmTick = BASE_TICK_MS * (BASE_BPM / bpm);
  const speed = Math.max(50, bpmTick - (level - 1) * 30);

  // Music control
  useEffect(() => {
    if (running && !gameOver && musicOn) {
      if (musicRef.current) { musicRef.current.stop(); musicRef.current = null; }
      musicRef.current = new KorobeinikiPlayer(bpm);
      musicRef.current.start();
    } else {
      if (musicRef.current) { musicRef.current.stop(); musicRef.current = null; }
    }
    return () => { if (musicRef.current) { musicRef.current.stop(); musicRef.current = null; } };
  }, [running, gameOver, musicOn, bpm]);

  const spawnPiece = useCallback((nextP) => {
    const np = nextP || randomPiece();
    const next = randomPiece();
    if (!isValid(stateRef.current.board, np)) { setGameOver(true); setRunning(false); playGameOverMelody(); return; }
    setPiece(np);
    setNextPiece(next);
  }, []);

  const lock = useCallback(() => {
    const { board: b, piece: p, score: s, lines: ln } = stateRef.current;
    if (!p) return;
    const merged = merge(b, p);
    const { board: newBoard, cleared } = clearLines(merged);
    const pts = [0, 100, 300, 500, 800];
    setBoard(newBoard);
    setScore(s + (pts[cleared] || 0));
    setLines(ln + cleared);
    spawnPiece(null);
  }, [spawnPiece]);

  const tick = useCallback(() => {
    const { board: b, piece: p } = stateRef.current;
    if (!p) return;
    if (isValid(b, p, 1, 0)) setPiece(prev => ({ ...prev, row: prev.row + 1 }));
    else lock();
  }, [lock]);

  const startGame = useCallback(() => {
    const b = createEmptyBoard();
    setBoard(b);
    setScore(0);
    setLines(0);
    setGameOver(false);
    setRunning(true);
    stateRef.current = { board: b, piece: null, score: 0, lines: 0 };
    const np = randomPiece();
    const next = randomPiece();
    if (!isValid(b, np)) { setGameOver(true); setRunning(false); playGameOverMelody(); return; }
    setPiece(np);
    setNextPiece(next);
  }, []);

  useEffect(() => {
    if (running && !gameOver) {
      tickRef.current = setInterval(tick, speed);
      return () => clearInterval(tickRef.current);
    }
    return () => clearInterval(tickRef.current);
  }, [running, gameOver, tick, speed]);

  const move = useCallback((dr, dc) => {
    const { board: b, piece: p } = stateRef.current;
    if (!p || !running || gameOver) return;
    if (isValid(b, p, dr, dc)) setPiece(prev => ({ ...prev, row: prev.row + dr, col: prev.col + dc }));
  }, [running, gameOver]);

  const rotatePiece = useCallback(() => {
    const { board: b, piece: p } = stateRef.current;
    if (!p || !running || gameOver) return;
    const rotated = rotate(p.shape);
    if (isValid(b, p, 0, 0, rotated)) setPiece(prev => ({ ...prev, shape: rotated }));
  }, [running, gameOver]);

  const hardDrop = useCallback(() => {
    const { board: b, piece: p } = stateRef.current;
    if (!p || !running || gameOver) return;
    let dr = 0;
    while (isValid(b, p, dr + 1, 0)) dr++;
    setPiece(prev => ({ ...prev, row: prev.row + dr }));
    setTimeout(lock, 10);
  }, [running, gameOver, lock]);

  useEffect(() => {
    const handler = (e) => {
      if (['ArrowLeft','ArrowRight','ArrowDown','ArrowUp',' '].includes(e.key)) e.preventDefault();
      switch (e.key) {
        case 'ArrowLeft': move(0, -1); break;
        case 'ArrowRight': move(0, 1); break;
        case 'ArrowDown': move(1, 0); break;
        case 'ArrowUp': rotatePiece(); break;
        case ' ': hardDrop(); break;
        default: break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [move, rotatePiece, hardDrop]);

  const display = piece ? merge(board, piece) : board;

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Moscow Background */}
      <div className="absolute inset-0">
        <img src={MOSCOW_BG} alt="Moscow" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/75" />
      </div>

      <div className="relative z-10 py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 px-4 py-2 bg-black/60 hover:bg-black/80 text-gray-300 hover:text-white rounded-lg transition-colors backdrop-blur-sm border border-white/10" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" /> {l.back}
            </button>
            <div className="flex items-center gap-3">
              {/* BPM control */}
              <div className="flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded-lg border border-white/10 px-2 py-1">
                <button onClick={() => setBpm(b => Math.max(80, b - 20))} className="p-1.5 text-gray-400 hover:text-white transition-colors" data-testid="bpm-minus">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="text-sm text-red-300 font-mono w-16 text-center" data-testid="bpm-display">{bpm} BPM</span>
                <button onClick={() => setBpm(b => Math.min(500, b + 20))} className="p-1.5 text-gray-400 hover:text-white transition-colors" data-testid="bpm-plus">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {/* Music toggle */}
              <button
                onClick={() => setMusicOn(m => !m)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors backdrop-blur-sm border border-white/10 ${musicOn ? 'bg-red-900/60 hover:bg-red-900/80 text-red-300' : 'bg-black/60 hover:bg-black/80 text-gray-400'}`}
                data-testid="music-toggle-btn"
              >
                {musicOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl font-light text-white text-center mb-8 tracking-wide drop-shadow-lg">{l.title}</h1>

          <div className="flex flex-col md:flex-row items-start justify-center gap-6">
            {/* Board */}
            <div className="relative border-2 border-red-500/40 rounded-lg overflow-hidden bg-black/70 backdrop-blur-md shadow-[0_0_40px_rgba(220,38,38,0.15)]" style={{ width: COLS * CELL + 4, height: ROWS * CELL + 4 }}>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${COLS}, ${CELL}px)`, gridTemplateRows: `repeat(${ROWS}, ${CELL}px)`, padding: 2 }}>
                {display.flat().map((cell, i) => (
                  <div key={i} style={{ width: CELL, height: CELL, backgroundColor: cell || 'rgba(0,0,0,0.5)', border: `1px solid ${cell ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.05)'}`, borderRadius: 2, boxShadow: cell ? `inset 0 0 6px ${cell}40` : 'none' }} />
                ))}
              </div>
              {gameOver && (
                <div className="absolute inset-0 bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center gap-4 z-10">
                  <p className="text-3xl font-bold text-red-400">{l.gameOver}</p>
                  <p className="text-xl text-[#d4c5a9]">{l.score}: {score}</p>
                  <button onClick={startGame} className="px-6 py-3 bg-red-700 hover:bg-red-600 text-white font-medium rounded-lg transition-colors flex items-center gap-2" data-testid="restart-btn">
                    <RotateCw className="w-5 h-5" /> {l.restart}
                  </button>
                </div>
              )}
              {!running && !gameOver && (
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-10">
                  <button onClick={startGame} className="px-8 py-4 bg-red-700 hover:bg-red-600 text-white font-bold text-xl rounded-lg transition-all hover:scale-105 flex items-center gap-3 shadow-[0_0_30px_rgba(220,38,38,0.4)]" data-testid="start-btn">
                    <Play className="w-6 h-6" /> {l.start}
                  </button>
                </div>
              )}
            </div>

            {/* Side Panel */}
            <div className="flex flex-col gap-4 min-w-[160px]">
              {/* Next Piece */}
              <div className="bg-black/60 backdrop-blur-md border border-red-500/20 rounded-lg p-4">
                <h3 className="text-sm text-gray-400 mb-2 text-center">{l.next}</h3>
                <div className="flex justify-center">
                  {nextPiece && (
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${nextPiece.shape[0].length}, 20px)` }}>
                      {nextPiece.shape.flat().map((v, i) => (
                        <div key={i} style={{ width: 20, height: 20, backgroundColor: v ? nextPiece.color : 'transparent', border: v ? '1px solid rgba(255,255,255,0.2)' : 'none', borderRadius: 2 }} />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="bg-black/60 backdrop-blur-md border border-red-500/20 rounded-lg p-4 space-y-3">
                <div className="text-center">
                  <p className="text-xs text-gray-500">{l.score}</p>
                  <p className="text-2xl font-bold text-red-400">{score}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">{l.lines}</p>
                  <p className="text-xl font-bold text-white">{lines}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">{l.level}</p>
                  <p className="text-xl font-bold text-white">{level}</p>
                </div>
              </div>

              {/* Pause Button */}
              {running && !gameOver && (
                <button onClick={() => setRunning(r => !r)} className="px-4 py-2 bg-black/60 backdrop-blur-sm hover:bg-black/80 text-white rounded-lg flex items-center justify-center gap-2 transition-colors border border-white/10" data-testid="pause-btn">
                  <Pause className="w-4 h-4" /> {l.pause}
                </button>
              )}

              {/* Controls */}
              <div className="bg-black/60 backdrop-blur-md border border-red-500/20 rounded-lg p-4">
                <h3 className="text-sm text-gray-400 mb-2 text-center">{l.controls}</h3>
                <div className="text-xs text-gray-500 space-y-1">
                  <p><span className="text-red-400 font-mono">{'<-'}</span> {l.left}</p>
                  <p><span className="text-red-400 font-mono">{'->'}</span> {l.right}</p>
                  <p><span className="text-red-400 font-mono">{'v'}</span> {l.down}</p>
                  <p><span className="text-red-400 font-mono">{'up'}</span> {l.rotate}</p>
                  <p><span className="text-red-400 font-mono">{'_'}</span> {l.drop}</p>
                </div>
              </div>

              {/* Mobile Controls */}
              <div className="md:hidden grid grid-cols-3 gap-2">
                <button onClick={() => move(0, -1)} className="p-3 bg-black/60 backdrop-blur-sm rounded-lg text-white text-center active:bg-red-900/60 border border-white/10">{'<'}</button>
                <button onClick={rotatePiece} className="p-3 bg-black/60 backdrop-blur-sm rounded-lg text-white text-center active:bg-red-900/60 border border-white/10">{'R'}</button>
                <button onClick={() => move(0, 1)} className="p-3 bg-black/60 backdrop-blur-sm rounded-lg text-white text-center active:bg-red-900/60 border border-white/10">{'>'}</button>
                <div />
                <button onClick={() => move(1, 0)} className="p-3 bg-black/60 backdrop-blur-sm rounded-lg text-white text-center active:bg-red-900/60 border border-white/10">{'v'}</button>
                <button onClick={hardDrop} className="p-3 bg-red-900/40 backdrop-blur-sm rounded-lg text-red-300 text-center active:bg-red-900/60 text-xs font-bold border border-red-500/20">{l.drop}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tetris;
